import { api } from '@/api';
import {
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  instanceOfPtwQaEquipmentWithRegistrationDto,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  PtwQaEquipmentWithRegistrationDto,
  QaEquipmentRtSystemDto,
  UnicheckDeviceDto,
  UnicheckSimulatorDto,
} from '@/api/generator';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { SortItem } from '@/components/OverviewTable/types.ts';
import { truthy } from '@/utils/helper.ts';
import { buildOdataQuery } from '@/utils/odata.ts';
import { isIOTDevice } from '@/utils/rtSystem';
import { QaEquipmentAddFilterObject } from '@/views/qaEquipment/types.ts';
import { useToggle } from '@vueuse/core';
import { t } from 'i18next';
import { MaybeRef } from 'vue';

export const NO_BRAND = 'NO_BRAND';

export function useDeleteQaEquipmentWithConfirmation() {
  return useActionWithConfirmation({
    confirmedAction: async (id: string) =>
      await api.qaEquipmentApi.deleteQaEquipmentRtSystem({
        id,
      }),
    confirmationOptions: {
      title: 'Really',
      text: 'Do you want to delete this QA-Equipment Entity?',
    },
  });
}

export type RegisterableQaEquipment =
  | PtwQaEquipmentWithRegistrationDto
  | UnicheckDeviceDto
  | UnicheckSimulatorDto;

export function isRegistrablePtwQaEquipment(value: object): value is RegisterableQaEquipment {
  return (
    instanceOfPtwQaEquipmentWithRegistrationDto(value) ||
    instanceOfUnicheckDeviceDto(value) ||
    instanceOfUnicheckSimulatorDto(value)
  );
}

type CreateQaEquipmentDtos =
  | QaEquipmentRtSystemDto
  | CreateQaEquipmentRtSystemDto
  | CreateOtherQaEquipmentDto;

function isSimulator(qaEquipment: QaEquipmentRtSystemDto) {
  return qaEquipment.subModel === 'Simulator';
}

export function getModelLabel(qaEquipment: Partial<CreateQaEquipmentDtos>) {
  const parts = [];
  if (qaEquipment.model) parts.push(qaEquipment.model);
  if (qaEquipment.subModel) parts.push(qaEquipment.subModel);
  return parts.join(' | ');
}

export function getManufacturerLabel(qaEquipment: Partial<CreateQaEquipmentDtos>) {
  return `RtSystemManufacturerEnum.${qaEquipment.manufacturer}`;
}

export function getTypeLabel(qaEquipment: Partial<CreateQaEquipmentDtos>) {
  return `QaEquipmentSubCategoryEnum.${qaEquipment.subCategory}`;
}

export function isIotDeviceWithoutLicense(
  qaEquipment: QaEquipmentRtSystemDto,
  allDeviceLicensesMasterDeviceIds: string[],
): boolean {
  return (
    (isIOTDevice(qaEquipment) &&
      !isSimulator(qaEquipment) &&
      qaEquipment.masterDeviceId &&
      !allDeviceLicensesMasterDeviceIds.some((id) => id === qaEquipment.masterDeviceId)) ||
    false
  );
}

type UseQaEquipmentRegistrationDialogOptions = {
  immediate: boolean;
};

export function useQaEquipmentRegistrationDialog(
  entity: MaybeRef<RegisterableQaEquipment | undefined>,
  options: UseQaEquipmentRegistrationDialogOptions = {
    immediate: false,
  },
) {
  const { immediate } = options;

  const [isRegistrationDialogOpen, toggleRegistrationDialog] = useToggle(false);
  const [isAssignmentDialogOpen, toggleAssignmentDialog] = useToggle(false);
  const [isSupportDialogOpen, toggleSupportDialog] = useToggle(false);

  if (immediate) {
    toggleRegistrationDialog(truthy(entity));
  }

  function requestAssignment() {
    toggleRegistrationDialog(false);
    toggleAssignmentDialog(true);
  }

  function requestSupport() {
    toggleRegistrationDialog(false);
    toggleSupportDialog(true);
  }

  return {
    requestAssignment,
    requestSupport,
    isRegistrationDialogOpen,
    isAssignmentDialogOpen,
    isSupportDialogOpen,
    toggleRegistrationDialog,
    toggleAssignmentDialog,
    toggleSupportDialog,
  };
}

export const successfullyRegisteredMsg = (qaEquipment: RegisterableQaEquipment) =>
  `${qaEquipment?.name} | ${qaEquipment?.iuid} ${t('Registered')}`;

export const successfullyDeRegisteredMsg = (qaEquipment: RegisterableQaEquipment) =>
  `${qaEquipment?.name} | ${qaEquipment?.iuid} ${t('DeRegistered')}`;

export function getPatchedQuery(orderBy: SortItem[], filter: QaEquipmentAddFilterObject) {
  const orderByPropertyName = orderBy
    .map((sortItem) => `${sortItem.key} ${sortItem.order}`)
    .join(',');
  const hasNoBrand = filter.brand1?.includes(NO_BRAND);
  if (hasNoBrand) {
    // move custom value to the end of the list, this is needed because for some
    // reason ODATA needs to have the null value as last element of the search
    filter.brand1?.splice(filter.brand1?.indexOf(NO_BRAND), 1);
    filter.brand1?.push(NO_BRAND);
  }
  const filterQuery = buildOdataQuery({
    orderBy: orderByPropertyName,
    search: filter.search,
    filter: {
      and: [
        {
          brand1: {
            in: filter.brand1,
          },
        },
        { subCategory: { in: filter.subCategory } },
      ],
    },
  });
  filterQuery.$filter = filterQuery.$filter?.replace(`'${NO_BRAND}'`, 'null');
  return filterQuery;
}
